import CardSymphony from 'elements/CardSymphony';
import { clsx, httpsLink, styx } from 'modules/Utils';
import { CSSProperties } from 'react';
import WebsiteBuilderHeader from '../WebsiteBuilderHeader';
import useWebsiteBuilder, { ExternalLinksStyle, WebsiteBuilder_externalLinksExists } from 'Hooks/useWebsiteBuilder';
import ButtonSymphony from 'elements/ButtonSymphony';
import PlatformLink from 'elements/PlatformLink';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import { DraggableList } from 'components/shareable/DraggableList';
import OptionsSymphony, { OptionItem } from 'elements/OptionsSymphony';
import { StackedWebsiteLayoutIcon, TraditionalWebsiteLayoutIcon } from 'components/svg-icons/website/themes/icons';
import { BoxesWebsiteLayoutIcon } from 'components/svg-icons/website/themes/icons';
import WebsiteBuilderFooter from '../WebsiteBuilderFooter';
import WebsiteBuilderEmptyState from '../WebsiteBuilderEmptyState';
import useScreen from 'Hooks/useScreen';

interface ExternalLinksContentProps {
    className: string;
    contentIndex: number;
    tabSelectorComponent: React.ReactNode;
    id: string;
}

const ExternalLinksContent = ({
    className,
    contentIndex,
    tabSelectorComponent,
    id,
}: ExternalLinksContentProps) => {
    const { mobileView } = useScreen();
    const {
        websiteContent,
        setWebsiteContent,
    } = useWebsiteBuilder() as WebsiteBuilder_externalLinksExists;

    const externalLinksContent = websiteContent[contentIndex].externalLinksContent;
    const externalLinksContentStyle = externalLinksContent.style;
    const requiresPaidPlan = externalLinksContentStyle === ExternalLinksStyle.BOXES || externalLinksContentStyle === ExternalLinksStyle.HYPERLINKS;

    const platforms = externalLinksContent.platforms;

    const EXTERNAL_LINKS_STYLE_OPTIONS: OptionItem[] = [
        {
            id: ExternalLinksStyle.STACKED_ROUNDED,
            label: 'Stacked Rounded',
            childrenLeft: (
                <StackedWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={externalLinksContent.style === ExternalLinksStyle.STACKED_ROUNDED}
                />
            )
        },
        {
            id: ExternalLinksStyle.STACKED_SQUARES,
            label: 'Stacked Squares',
            childrenLeft: (
                <StackedWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={externalLinksContent.style === ExternalLinksStyle.STACKED_SQUARES}
                />
            )
        },
        {
            id: ExternalLinksStyle.HYPERLINKS,
            label: 'Hyperlinks',
            iconRight: 'gem',
            childrenLeft: (
                <TraditionalWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={externalLinksContent.style === ExternalLinksStyle.HYPERLINKS}
                />
            )
        },
        {
            id: ExternalLinksStyle.BOXES,
            label: 'Boxes',
            iconRight: 'gem',
            childrenLeft: (
                <BoxesWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={externalLinksContent.style === ExternalLinksStyle.BOXES}
                />
            )
        },
    ];

    const isSecondaryColor = () => {
        switch (externalLinksContentStyle) {
            case ExternalLinksStyle.BOXES:
            case ExternalLinksStyle.STACKED_SQUARES:
            case ExternalLinksStyle.STACKED_ROUNDED:
                return true;
            default:
                return false;
        }
    }

    const handleAddLink = () => {
        const websiteContentLocal = [...websiteContent];
        websiteContentLocal[contentIndex].externalLinksContent.platforms.push({
            name: `external-${Date.now()}`,
            title: 'External Link',
            description: 'External Link',
            icon: 'link',
            link: '',
            isEnabled: true,
            placeholder: 'https://www.example.com'
        });
        setWebsiteContent(websiteContentLocal);
    }

    const renderRightSideContent = () => {
        if (mobileView) {
            return (
                <Icon
                    className="externalLinksContentAddLinkButton"
                    icon="addCircle"
                    size={24}
                    onClick={handleAddLink}
                />
            )
        }

        return (
            <ButtonSymphony
                className="externalLinksContentAddLinkButton"
                iconLeft="addCircle"
                onClick={handleAddLink}
                variant="text"
            >
                Add Link
            </ButtonSymphony>
        )
    }

    // NOTE: render()
    return (
        <CardSymphony
            className={clsx("externalLinksContentComponent", className)}
            style={styx(sty.externalLinksContentComponent)}
            size="small"
            id={id}
        >
            <WebsiteBuilderHeader
                contentIndex={contentIndex}
                rightSideContent={
                    <>
                        {tabSelectorComponent}
                        {renderRightSideContent()}
                    </>
                }
            />

            {!platforms.length &&
                <WebsiteBuilderEmptyState
                    className="externalLinksContentEmptyState"
                    title="No external links added"
                    description="Add an external link to your website to get started."
                    isHorizontal
                    buttonText="Add Link"
                    onClickButton={handleAddLink}
                />
            }

            <DraggableList
                id={`externalLinksContentDraggableList-${externalLinksContent.title}`}
                list={platforms}
                keyOfItem="name"
                isDragHandle
                updateList={(newList) => {
                    const websiteContentLocal = [...websiteContent];
                    websiteContentLocal[contentIndex].externalLinksContent.platforms = newList;
                    setWebsiteContent(websiteContentLocal);
                }}
                renderItem={(platform, index, dragHandleProps) => {
                    return (
                        <div
                            className="externalLinksContentPlatformLinkContainer"
                            style={sty.externalLinksContentPlatformLinkContainer}
                        >
                            <div
                                {...dragHandleProps}
                                className="externalLinksContentPlatformLinkDragHandle"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'grab',
                                }}
                            >
                                <Icon
                                    className="externalLinksContentPlatformLinkDragHandleIcon"
                                    icon="drag"
                                    size={24}
                                    color={Colors.grey}
                                />
                            </div>
                            <PlatformLink
                                className="externalLinksContentPlatformLink"
                                title={platform.title}
                                description={platform.description}
                                icon={platform.icon}
                                link={platform.link}
                                isEnabled={platform.isEnabled}
                                placeholder={platform.placeholder}
                                isRow={!mobileView}
                                isIcon={!mobileView}
                                onBlur={(newLink: string) => {
                                    const websiteContentLocal = [...websiteContent];
                                    websiteContentLocal[contentIndex].externalLinksContent.platforms[index].link = httpsLink(newLink);
                                    setWebsiteContent(websiteContentLocal);
                                }}
                                onChangeTitle={(newTitle: string) => {
                                    const websiteContentLocal = [...websiteContent];
                                    websiteContentLocal[contentIndex].externalLinksContent.platforms[index].title = newTitle;
                                    setWebsiteContent(websiteContentLocal);
                                }}
                                onRemove={() => {
                                    const websiteContentLocal = [...websiteContent];
                                    websiteContentLocal[contentIndex].externalLinksContent.platforms.splice(index, 1);
                                    setWebsiteContent(websiteContentLocal);
                                }}
                                onChange={(newLink: string) => {
                                    const websiteContentLocal = [...websiteContent];
                                    websiteContentLocal[contentIndex].externalLinksContent.platforms[index].link = newLink;
                                    setWebsiteContent(websiteContentLocal);
                                }}
                                onToggleEnabled={() => {
                                    const websiteContentLocal = [...websiteContent];
                                    websiteContentLocal[contentIndex].externalLinksContent.platforms[index].isEnabled =
                                        !websiteContentLocal[contentIndex].externalLinksContent.platforms[index].isEnabled;
                                    setWebsiteContent(websiteContentLocal);
                                }}
                            />
                        </div>
                    )
                }}
            />

            <WebsiteBuilderFooter
                className="externalLinksContentFooter"
                contentIndex={contentIndex}
                isBackgroundColor
                isSecondaryColor={isSecondaryColor()}
                isTextColor
                requiresPaidPlan={requiresPaidPlan}
                titleRequired="This external links style requires a paid plan to publish your website."
                styleOptions={
                    <OptionsSymphony
                        className="externalLinksContentStyleOptions"
                        style={{ flexWrap: 'wrap' }}
                        options={EXTERNAL_LINKS_STYLE_OPTIONS}
                        value={externalLinksContentStyle || ''}
                        handleOptionSelected={(option) => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].externalLinksContent.style = option as ExternalLinksStyle;
                            setWebsiteContent(updatedContent);
                        }}
                        size="small"
                        isRadio={false}
                        childrenLeftOffset={-4}
                    />
                }
            />
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    externalLinksContentComponent: {
        display: 'flex',
        flexDirection: 'column',
    },
    externalLinksContentPlatformLinkContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        border: Sty.border,
        borderRadius: 8,
        padding: 8,
    }
}

export default ExternalLinksContent;